import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Button } from './Button';
import PDFViewer from './PDFViewer';
import { cn } from '../../lib/utils';

interface PDFModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
  pdfContent?: string;
  pdfFile?: File;
  title?: string;
}

const PDFModal: React.FC<PDFModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  pdfContent,
  pdfFile,
  title = 'PDF Document'
}) => {
  const [isClosing, setIsClosing] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200); // Match animation duration
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleOpenExternal = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm',
        'flex items-center justify-center p-2 sm:p-4',
        isClosing ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          'relative bg-white rounded-lg shadow-2xl',
          'w-full h-full max-w-6xl max-h-full',
          'flex flex-col overflow-hidden',
          isClosing 
            ? 'animate-out zoom-out-95 duration-200' 
            : 'animate-in zoom-in-95 duration-200'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-lg font-semibold text-gray-900 truncate">
              {title}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3">
            {pdfUrl && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleOpenExternal}
                title="Open in new tab"
                className="h-9 w-9"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={handleClose}
              title="Close"
              className="h-9 w-9"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 bg-gray-100 min-h-0">
          <PDFViewer
            pdfUrl={pdfUrl}
            pdfContent={pdfContent}
            pdfFile={pdfFile}
            title={title}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
};

export default PDFModal;